import { ArrowRight, MessageCircle } from 'lucide-react';
import styles from './AeternaLoopContext.module.css';

export type AeternaLoopSourceType =
  | 'metric'
  | 'report'
  | 'practice'
  | 'podcast'
  | 'course'
  | 'manual';

export type AeternaLoopStage = 'see' | 'understand' | 'act' | 'ask';

export interface AeternaLoopContextValue {
  sourceType: AeternaLoopSourceType;
  sourceTitle: string;
  sourceId?: string;
  sourceDate?: string;
  stage: AeternaLoopStage;
  evidenceBoundary: string;
  askPrompt?: string;
}

const SOURCE_LABELS: Record<AeternaLoopSourceType, string> = {
  metric: '指标线索',
  report: '体检报告',
  practice: '实践反馈',
  podcast: '当前播客',
  course: '通识课',
  manual: '手动记录',
};

const STAGE_LABELS: Record<AeternaLoopStage, string> = {
  see: '照见来源',
  understand: '真知解释',
  act: '践行安排',
  ask: '解惑上下文',
};

export function getLoopSourceLabel(sourceType: AeternaLoopSourceType) {
  return SOURCE_LABELS[sourceType];
}

export function getLoopStageLabel(stage: AeternaLoopStage) {
  return STAGE_LABELS[stage];
}

export function buildAskContextPath(context: AeternaLoopContextValue) {
  const params = new URLSearchParams();
  params.set('contextType', context.sourceType);
  params.set('contextTitle', context.sourceTitle);
  params.set('contextStage', context.stage);
  params.set('contextBoundary', context.evidenceBoundary);
  if (context.sourceId) params.set('sourceId', context.sourceId);
  if (context.sourceDate) params.set('sourceDate', context.sourceDate);
  if (context.askPrompt) params.set('contextPrompt', context.askPrompt);
  return `/ask?${params.toString()}`;
}

interface EvidenceMetaProps {
  context: AeternaLoopContextValue;
  compact?: boolean;
  showTitle?: boolean;
  className?: string;
}

export function EvidenceMeta({
  context,
  compact = false,
  showTitle = true,
  className,
}: EvidenceMetaProps) {
  const classNames = [
    styles.evidenceMeta,
    compact ? styles.evidenceMetaCompact : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classNames}>
      <span className={styles.evidenceLabel}>{getLoopSourceLabel(context.sourceType)}</span>
      {showTitle && <strong>{context.sourceTitle}</strong>}
      {context.sourceDate && <span>{context.sourceDate}</span>}
      <span className={styles.evidenceBoundary}>{context.evidenceBoundary}</span>
    </div>
  );
}

interface ContextAskButtonProps {
  context: AeternaLoopContextValue;
  label?: string;
  className?: string;
  onNavigate: (path: string) => void;
}

export function ContextAskButton({
  context,
  label = '带着这个问题去解惑',
  className,
  onNavigate,
}: ContextAskButtonProps) {
  return (
    <button
      className={className ?? styles.contextButtonSecondary}
      type="button"
      onClick={() => onNavigate(buildAskContextPath(context))}
    >
      <MessageCircle size={16} strokeWidth={1.5} aria-hidden />
      {label}
    </button>
  );
}

interface TodayContextPanelProps {
  context: AeternaLoopContextValue;
  askLabel?: string;
  primaryAction?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
  onNavigate: (path: string) => void;
}

export function TodayContextPanel({
  context,
  askLabel,
  primaryAction,
  className,
  onNavigate,
}: TodayContextPanelProps) {
  const classNames = [styles.contextPanel, className ?? ''].filter(Boolean).join(' ');

  return (
    <section className={classNames} aria-label="今日上下文">
      <div className={styles.contextTop}>
        <p className={styles.contextEyebrow}>{getLoopStageLabel(context.stage)}</p>
        <span className={styles.evidenceLabel}>{getLoopSourceLabel(context.sourceType)}</span>
      </div>
      <h2 className={styles.contextTitle}>{context.sourceTitle}</h2>
      <p className={styles.contextBoundary}>{context.evidenceBoundary}</p>
      <div className={styles.contextActions}>
        {primaryAction && (
          <button className={styles.contextButton} type="button" onClick={primaryAction.onClick}>
            {primaryAction.label}
            <ArrowRight size={16} strokeWidth={1.5} aria-hidden />
          </button>
        )}
        <ContextAskButton context={context} label={askLabel} onNavigate={onNavigate} />
      </div>
    </section>
  );
}

interface MissingDataStateProps {
  label?: string;
  detail?: string;
}

export function MissingDataState({
  label = '未同步',
  detail = '同步或手动记录后显示',
}: MissingDataStateProps) {
  return (
    <div className={styles.missingData}>
      <span className={styles.missingLabel}>缺数据</span>
      <strong className={styles.missingValue}>{label}</strong>
      <p className={styles.missingDetail}>{detail}</p>
    </div>
  );
}
